/**
 * "Peek at the bones": after a hand (and after the last hand of the game), a player may see the card that was
 * on top of the bones straight after their final turn — the card they'd have drawn had they drawn
 * once more. It changes nothing that happened: the hand's result, the state and the hand points
 * stand. The analysis says what the best one-card swap for that card would have made, and whether
 * that would have beaten (or tied, rank for rank) the hand that won. It's hypothetical.
 */
import type { Card } from "./cards.ts";
import { evaluate, compareHands, type HandCategory, type HandRank } from "./handEval.ts";

/** Peeks are free (Scott, 1 Nov 2026). They cost 10 points before; the Hall of Fame keeps what was spent then. */
export const PEEK_COST = 0;

export interface PeekAnalysis {
  /** Your five cards with the peeked card swapped in where it helps most (your own hand if nothing helps). */
  best: Card[];
  bestCategory: HandCategory;
  /** The swap would have made a better hand than the one you held. */
  improves: boolean;
  /** You won the hand anyway. */
  wasWinner: boolean;
  /** The best swap would have beaten the winning hand (never true for the winner). */
  beatsWinner: boolean;
  /** Same category and ranks as the winning hand (only the suits decide between them). */
  tiesWinner: boolean;
}

const sameRanks = (a: HandRank, b: HandRank) => a.category === b.category && a.tiebreak.join() === b.tiebreak.join();

export function analysePeek(hand: readonly Card[], card: Card, winningHand: readonly Card[], wasWinner: boolean): PeekAnalysis {
  const own = evaluate(hand);
  let best = [...hand], bestRank = own;
  for (let i = 0; i < hand.length; i++) {
    const trial = hand.map((c, k) => (k === i ? card : c));
    const r = evaluate(trial);
    if (compareHands(r, bestRank) > 0) { best = trial; bestRank = r; }
  }
  const win = evaluate(winningHand);
  const improves = compareHands(bestRank, own) > 0;
  return {
    best: best.map((c) => ({ ...c })), bestCategory: bestRank.category, improves, wasWinner,
    beatsWinner: !wasWinner && compareHands(bestRank, win) > 0,
    tiesWinner: !wasWinner && improves && sameRanks(bestRank, win),
  };
}
